// src/pages/FacultyReport.jsx
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, mapRow, mapRows } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { loadAllSections } from "../utils/sectionUtils";
import { SECTIONS, SECTION_FIELDS } from "../constants/sections";
import DashboardHeader from "../components/DashboardHeader";
import { useReactToPrint } from "react-to-print";
import { downloadPDF, formatDuration } from "../utils/reportUtils";

const REPORT_COLUMNS = {
  articlePublications: ["","Si No","Title of Article","Journal Details","Author Name and Position","DCI Category and Impact Factor","Acceptance Details","Month and Year of Publication","Points","Current Status","Link for Article"],
  textBookPublications: ["","Si No","Title of Text Book","Publisher Details","Author Name","DCI Category and Impact Factor","Acceptance Details","Month and Year of Publication","Points","Current Status","Link for Text Book"],
  patents: ["","Si No","Title of Patent","Application Submission Details","Author Name","Acceptance Details","Month and Year of Publication","Points","Current Status","Link for Patent"],
  copyrights: ["","Si No","Title","Application Submission Details","Author Name","Acceptance Details","Month and Year of Publication","Points","Current Status","Link for Copyright"],
  fellowships: ["","Si No","Title","Fellowship Registration Details","Faculty Name","Month and Year of Initiation","Month and Year of Completion","Points","Current Status","Link for Fellowship"],
  phd: ["","Si No","Title","PhD Registration Details","Faculty Name","Month and Year of Initiation","Month and Year of Completion","Points","Current Status","Link for PhD"],
  swayamCourses: ["","Si No","Title","Swayam Registration Details","Faculty Name","Month and Year of Initiation","Month and Year of Completion","Points","Current Status","Link for Course"],
  awards: ["","Si No","Award Name","Award Given By","Faculty Name","Month and Date","Current Status","Link for Award"],
  innovativeTeaching: ["","Si No","Faculty Name","Month and Date","Topic","Innovation Details","Theory or Practical Session","Link for Session"],
  eContent: ["","Si No","Faculty Name","Month and Date","Topic","E-Content Details","Theory or Practical Session","Link for Content"],
  researchGrants: ["","Si No","Faculty Name","Month and Date","Topic","Grant Details","Funding Agency Details","Link for Grant"],
  guestLectures: ["","Si No","Faculty Name","Month and Date","Topic","Guest Lecture Details","Organized By","Link Details"],
  conferencePresentation: ["","Si No","Faculty Name","Month and Date","Topic","Presentation Details","Organized By","Link"],
  shortResearchProjects: ["","Si No","Faculty Name","Month and Date","Topic","Proposal Details","Investigator Details","Link Details"],
};

const COLUMN_KEYS = {
  articlePublications: ["si_no","title_of_article","journal_details","author_name_and_position","dci_category_and_impact_factor","acceptance_details","month_and_year_of_publication","points","current_status","link_for_article"],
  textBookPublications: ["si_no","title_of_text_book","publisher_details","author_name","dci_category_and_impact_factor","acceptance_details","month_and_year_of_publication","points","current_status","link_for_text_book"],
  patents: ["si_no","title_of_patent","application_submission_details","author_name","acceptance_details","month_and_year_of_publication","points","current_status","link_for_patent"],
  copyrights: ["si_no","title","application_submission_details","author_name","acceptance_details","month_and_year_of_publication","points","current_status","link_for_copyright"],
  fellowships: ["si_no","title","fellowship_registration_details","faculty_name","month_and_year_of_initiation","month_and_year_of_completion","points","current_status","link_for_fellowship"],
  phd: ["si_no","title","phd_registration_details","faculty_name","month_and_year_of_initiation","month_and_year_of_completion","points","current_status","link_for_phd"],
  swayamCourses: ["si_no","title","swayam_registration_details","faculty_name","month_and_year_of_initiation","month_and_year_of_completion","points","current_status","link_for_course"],
  awards: ["si_no","award_name","award_given_by","faculty_name","month_and_date","current_status","link_for_award"],
  innovativeTeaching: ["si_no","faculty_name","month_and_date","topic","innovation_details","session_type","link_for_session"],
  eContent: ["si_no","faculty_name","month_and_date","topic","e_content_details","session_type","link_for_content"],
  researchGrants: ["si_no","faculty_name","month_and_date","topic","grant_details","funding_agency_details","link_for_grant"],
  guestLectures: ["si_no","faculty_name","month_and_date","topic","guest_lecture_details","organized_by","link_details"],
  conferencePresentation: ["si_no","faculty_name","month_and_date","topic","presentation_details","organized_by","link"],
  shortResearchProjects: ["si_no","faculty_name","month_and_date","topic","proposal_details","investigator_details","link_details"],
};

export default function FacultyReport() {
  const { currentUser, logout, userRole } = useAuth();
  const navigate = useNavigate();
  const printRef = useRef();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("articlePublications");
  const [sectionEntries, setSectionEntries] = useState([]); // Real-time entries from API
  const [filterStatus, setFilterStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState("");
  const [submittedReports, setSubmittedReports] = useState([]);
  // Key: originalIndex (number), Value: boolean
  const [selectedEntries, setSelectedEntries] = useState({});

  const [allSections, setAllSections] = useState(SECTIONS);
  const [dynamicCols, setDynamicCols] = useState([]);
  const [dynamicKeys, setDynamicKeys] = useState([]);

  useEffect(() => { 
    fetchProfile(); 
    fetchSubmittedReports(); 
    loadAllSections().then(setAllSections);
  }, []);
  
  useEffect(() => { 
    setSelectedEntries({});
    fetchSectionEntries(); 
  }, [activeSection]);

  async function fetchSectionEntries() {
    try {
      setLoading(true);
      const res = await api.get(`/faculty/${currentUser.uid}/section/${activeSection}`);
      const entries = Array.isArray(res) ? res : [];
      setSectionEntries(entries);
    } catch (err) {
      console.error(`Error fetching ${activeSection} entries:`, err);
      setSectionEntries([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    async function fetchDynamicFields() {
      let cols = [];
      let keys = [];
      // Always attempt to fetch from Firestore first for any section (built-in or custom)
      try {
        const res = await api.get(`/section_fields_${activeSection}?orderBy=order`);
        const fieldRows = mapRows(res);
        if (fieldRows.length > 0) {
          const fields = fieldRows;
          // Standard first columns. The empty string "" corresponds to the checkbox column.
          cols = ["", "Si No", "Month & Year"];
          keys = ["si_no", "unified_date"];
          
          fields.forEach(f => {
            const k = f.key || f.name?.toLowerCase().replace(/[^a-z0-9]/g, "_");
            if (!["month_and_year_of_publication", "month_and_date", "month_and_year_of_initiation", "date", "month_and_year"].includes(k)) {
              cols.push(f.label || f.name);
              keys.push(k);
            }
          });
          
          setDynamicCols(cols);
          setDynamicKeys(keys);
        } else if (REPORT_COLUMNS[activeSection]) {
          // If Firestore is empty, fall back to hardcoded columns for built-in sections
          setDynamicCols(REPORT_COLUMNS[activeSection]);
          setDynamicKeys(COLUMN_KEYS[activeSection]);
        } else {
          // Fallback for custom sections with no fields defined yet
          setDynamicCols(["", "Si No"]);
          setDynamicKeys(["si_no"]);
        }
      } catch (err) {
        console.error("Failed to fetch fields for report:", err);
        // Fallback on error
        if (REPORT_COLUMNS[activeSection]) {
          setDynamicCols(REPORT_COLUMNS[activeSection]);
          setDynamicKeys(COLUMN_KEYS[activeSection]);
        } else {
          setDynamicCols(["", "Si No"]);
          setDynamicKeys(["si_no"]);
        }
      }
    }
    fetchDynamicFields();
  }, [activeSection]);

  async function fetchProfile() {
    try {
      setLoading(true);
      const res = await api.get(`/faculty/${currentUser.uid}/profile`);
      if (res) setProfile(mapRow(res));
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function fetchSubmittedReports() {
    try {
      const res = await api.get("/submitted_reports");
      const reports = mapRows(res)
        .filter((r) => r.facultyId === currentUser.uid)
        .sort((a, b) => new Date(b.submittedAt || 0).getTime() - new Date(a.submittedAt || 0).getTime());
      setSubmittedReports(reports);
    } catch {}
  }

  // Returns a Set of originalIndexes that are already submitted for current section
  function getSubmittedIndexes() {
    const submitted = new Set();
    const sectionReports = submittedReports
      .filter(r => r.sectionKey === activeSection)
      .slice()
      .sort((a, b) => {
        // Sort oldest first so sequential blocks are assigned correctly
        const ta = a.submittedAt?.toDate ? a.submittedAt.toDate().getTime() : new Date(a.submittedAt || 0).getTime();
        const tb = b.submittedAt?.toDate ? b.submittedAt.toDate().getTime() : new Date(b.submittedAt || 0).getTime();
        return ta - tb;
      });

    let nextFallbackIdx = 0; // tracks the next index to assign for old records

    for (const r of sectionReports) {
      if (r.submittedIndexes && r.submittedIndexes.length > 0) {
        // Modern records: use stored indexes directly
        r.submittedIndexes.forEach(idx => submitted.add(idx));
        // Advance fallback pointer past any explicitly tracked indexes
        const maxIdx = Math.max(...r.submittedIndexes);
        if (maxIdx >= nextFallbackIdx) nextFallbackIdx = maxIdx + 1;
      } else if (r.totalEntries > 0) {
        // Old records: assign sequential block starting from nextFallbackIdx
        for (let i = nextFallbackIdx; i < nextFallbackIdx + r.totalEntries; i++) {
          submitted.add(i);
        }
        nextFallbackIdx += r.totalEntries;
      }
    }
    return submitted;
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 4000); }

  async function handleLogout() {
    await logout();
    navigate(userRole === "admin" || userRole === "superadmin" ? "/admin" : "/login");
  }

  function getEntryValue(entry, key) {
    if (key === "si_no") return "";
    if (key === "unified_date") {
      return entry.monthAndYearOfPublication || entry.month_and_year_of_publication || entry.monthAndDate || entry.month_and_date || entry.monthAndYearOfInitiation || entry.month_and_year_of_initiation || entry.date || entry.monthAndYear || entry.month_and_year || "—";
    }
    let val = entry[key];
    if (val === undefined) {
      const camel = key.replace(/_([a-z])/g, (_, l) => l.toUpperCase());
      val = entry[camel];
    }
    if (val === undefined) return "—";
    
    // Check if it's an experience field to apply formatting
    const keyLower = key.toLowerCase();
    if (keyLower.includes("exp") || keyLower.includes("duration")) {
      return formatDuration(val);
    }
    
    return val || "—";
  }

  function getFilteredRows() {
    if (!profile) return [];
    const entries = sectionEntries && sectionEntries.length > 0 ? sectionEntries : (profile[activeSection] || []);
    return entries
      .map((entry, originalIndex) => ({ entry, originalIndex }))
      .filter(({ entry }) => {
        if (!filterStatus) return true;
        const statusKey = Object.keys(entry).find(k => k.toLowerCase().includes("status"));
        const actualStatus = statusKey ? entry[statusKey] : entry.currentStatus;
        return actualStatus === filterStatus;
      })
      .map(({ entry, originalIndex }, displayIndex) => ({
        siNo: displayIndex + 1,
        entry,
        originalIndex,
      }));
  }

  function toggleEntry(originalIndex) {
    const submittedIndexes = getSubmittedIndexes();
    if (submittedIndexes.has(originalIndex)) return;
    setSelectedEntries(prev => ({ ...prev, [originalIndex]: !prev[originalIndex] }));
  }

  function toggleSelectAll() {
    const submittedIndexes = getSubmittedIndexes();
    const rows = getFilteredRows();
    const unsubmitted = rows.filter(r => !submittedIndexes.has(r.originalIndex));
    const allSelected = unsubmitted.every(r => selectedEntries[r.originalIndex]);
    const next = {};
    unsubmitted.forEach(r => { next[r.originalIndex] = !allSelected; });
    setSelectedEntries(next);
  }

  function getSelectedRows() {
    const submittedIdx = getSubmittedIndexes();
    // Exclude already-submitted entries — they cannot be re-submitted
    return getFilteredRows().filter(r => selectedEntries[r.originalIndex] && !submittedIdx.has(r.originalIndex));
  }

  async function handleSubmitReport() {
    const submittedIdx = getSubmittedIndexes();
    // Double-guard: remove any already-submitted entries from selection
    const selectedRows = getSelectedRows().filter(r => !submittedIdx.has(r.originalIndex));
    if (selectedRows.length === 0) {
      // Check if the user had checked only already-submitted rows
      const checkedCount = Object.values(selectedEntries).filter(Boolean).length;
      if (checkedCount > 0) {
        return showToast("⚠️ These entries are already submitted. Please select new (unsubmitted) entries only.");
      }
      return showToast("❌ Select at least one new entry to submit!");
    }
    try {
      setSubmitting(true);
      await api.post("/submitted_reports", {
        facultyId: currentUser.uid,
        facultyName: profile.name,
        facultyEmpId: profile.empId,
        facultyDept: profile.department,
        sectionKey: activeSection,
        sectionLabel: activeSec?.label,
        filterStatus: filterStatus || "All",
        totalEntries: selectedRows.length,
        submittedAt: new Date().toISOString(),
        submittedIndexes: selectedRows.map(r => r.originalIndex),
        reportData: selectedRows.map(({ siNo, entry }) => ({ siNo, ...entry })),
      });
      showToast(`✅ ${selectedRows.length} ${activeSec?.label} entries submitted to admin!`);
      setSelectedEntries({});
      fetchSubmittedReports();
    } catch (err) {
      showToast("❌ Failed to submit. Try again.");
    } finally { setSubmitting(false); }
  }

  const activeSec = allSections.find(s => s.key === activeSection);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${profile?.name}_${activeSec?.label}_Report`,
  });

  const handleDownloadPDF = async () => {
    setSubmitting(true);
    await downloadPDF(printRef, `${profile?.name}_${activeSec?.label}_Report.pdf`);
    setSubmitting(false);
  };

  const handleExportCSV = () => {
    const rows = getFilteredRows();
    const cols = (dynamicCols || []).slice(1);
    const keys = dynamicKeys || [];
    const sec = allSections.find(s => s.key === activeSection);
    let csv = `Faculty Name: ${profile?.name},Emp ID: ${profile?.empId},Department: ${profile?.department}\n\n`;
    csv += cols.join(",") + "\n";
    rows.forEach(({ siNo, entry }) => {
      const row = keys.map(key => {
        if (key === "si_no") return siNo;
        return `"${String(getEntryValue(entry, key)).replace(/"/g, '""')}"`;
      });
      csv += row.join(",") + "\n";
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${profile?.name}_${sec?.label}_Report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const filteredRows = getFilteredRows();
  const submittedIndexes = getSubmittedIndexes();
  const cols = dynamicCols || [];
  const keys = dynamicKeys || [];
  const statusOptions = ["Sent","Accepted","Published","Initiated","In Progress","Completed","Received","Nominated","Pending"];
  const selectedRows = getSelectedRows();
  const unsubmittedRows = filteredRows.filter(r => !submittedIndexes.has(r.originalIndex));
  const allUnsubmittedSelected = unsubmittedRows.length > 0 && unsubmittedRows.every(r => selectedEntries[r.originalIndex]);

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Nunito', sans-serif", fontSize: 20, color: "#64748b" }}>
      ⏳ Loading...
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f4f6fb", fontFamily: "'Nunito', sans-serif" }}>

      <DashboardHeader label="Faculty Portal" showNewsTicker={true}>
        {(userRole === "admin" || userRole === "superadmin") && (
          <button onClick={() => navigate("/admin/dashboard")} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 14px", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>🏠 Admin</button>
        )}
        <button onClick={() => navigate(userRole === "admin" || userRole === "superadmin" ? "/my-portfolio" : "/dashboard")} style={{ background: "#fff", color: "#1d4ed8", border: "none", borderRadius: 10, padding: "8px 18px", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>← My Portfolio</button>
        <button onClick={handleLogout} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>🚪 Logout</button>
      </DashboardHeader>

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", top: 80, right: 24, zIndex: 999, background: toast.startsWith("✅") ? "#f0fdf4" : "#fef2f2", border: `1px solid ${toast.startsWith("✅") ? "#86efac" : "#fecaca"}`, color: toast.startsWith("✅") ? "#16a34a" : "#dc2626", borderRadius: 12, padding: "14px 20px", fontWeight: 700, fontSize: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", maxWidth: 400 }}>
          {toast}
        </div>
      )}

      <div style={{ display: "flex", minHeight: "calc(100vh - 64px)" }}>

        {/* Sidebar */}
        <aside style={{ width: 230, background: "#fff", boxShadow: "2px 0 16px rgba(15,37,87,0.07)", paddingTop: 16, flexShrink: 0, overflowY: "auto" }}>
          <div style={{ padding: "8px 16px 12px", fontSize: 13, fontWeight: 700, color: "#94a3b8", letterSpacing: 1 }}>MY REPORT SECTIONS</div>
          {allSections.map(sec => {
            const count = profile?.[sec.key]?.length || 0;
            const submittedCount = submittedReports
              .filter(r => r.sectionKey === sec.key)
              .reduce((acc, r) => acc + (r.submittedIndexes?.length || r.totalEntries || 0), 0);
            return (
              <div key={sec.key} onClick={() => { setActiveSection(sec.key); setFilterStatus(""); setSelectedEntries({}); }}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", cursor: "pointer", borderLeft: activeSection === sec.key ? `3px solid ${sec.color}` : "3px solid transparent", background: activeSection === sec.key ? sec.light : "transparent", color: activeSection === sec.key ? sec.color : "#475569", fontWeight: activeSection === sec.key ? 700 : 400, fontSize: 15, transition: "all 0.15s" }}>
                <span style={{ fontSize: 17 }}>{sec.icon}</span>
                <span style={{ flex: 1, lineHeight: 1.3 }}>{sec.label}</span>
                {submittedCount > 0 && <span style={{ fontSize: 13 }}>✅</span>}
                <span style={{ background: activeSection === sec.key ? sec.color : "#e2e8f0", color: activeSection === sec.key ? "#fff" : "#64748b", borderRadius: 20, padding: "1px 7px", fontSize: 13, fontWeight: 700 }}>{count}</span>
              </div>
            );
          })}

          {submittedReports.length > 0 && (
            <div style={{ margin: "16px 12px 0", padding: "12px", background: "#f0fdf4", borderRadius: 12, border: "1px solid #86efac" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#16a34a", marginBottom: 8 }}>✅ SUBMITTED REPORTS</div>
              {submittedReports.slice(0, 5).map((r, i) => (
                <div key={i} style={{ fontSize: 13, color: "#475569", marginBottom: 4, padding: "4px 0", borderBottom: i < Math.min(submittedReports.length, 5) - 1 ? "1px solid #dcfce7" : "none" }}>
                  <div style={{ fontWeight: 600, color: "#16a34a" }}>{r.sectionLabel}</div>
                  <div style={{ color: "#94a3b8" }}>{r.totalEntries} entries · {r.submittedAt?.toDate ? new Date(r.submittedAt.toDate()).toLocaleDateString() : "—"}</div>
                </div>
              ))}
            </div>
          )}
        </aside>

        {/* Main */}
        <main style={{ flex: 1, padding: "24px", overflowX: "auto" }}>

          {/* Faculty Info Banner */}
          <div style={{ background: "linear-gradient(120deg,#0f2557,#2563eb)", borderRadius: 16, padding: "18px 24px", color: "#fff", marginBottom: 20, display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,#f7a84f,#f74f6e)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 900, border: "2px solid rgba(255,255,255,0.3)" }}>
              {profile?.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{profile?.name}</div>
              <div style={{ opacity: 0.75, fontSize: 14, marginTop: 3, display: "flex", gap: 14 }}>
                {profile?.designation && <span>🎓 {profile.designation}</span>}
                {profile?.department && <span>🏢 {profile.department}</span>}
                {profile?.empId && <span>🪪 {profile.empId}</span>}
              </div>
            </div>
            <div style={{ marginLeft: "auto", textAlign: "right" }}>
              <div style={{ fontSize: 13, opacity: 0.7 }}>Total Submitted Reports</div>
              <div style={{ fontSize: 26, fontWeight: 900 }}>{submittedReports.length}</div>
            </div>
          </div>

          {/* Title + Actions */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <h2 style={{ fontSize: 22, fontWeight: 900, color: "#0f2557", margin: 0 }}>
                {activeSec?.icon} {activeSec?.label} Report
              </h2>
              <p style={{ color: "#64748b", fontSize: 15, marginTop: 4 }}>
                {filteredRows.length} total entries
                {submittedIndexes.size > 0 && <span style={{ color: "#16a34a", marginLeft: 8 }}>· {submittedIndexes.size} already submitted</span>}
                {selectedRows.length > 0 && <span style={{ color: "#7c3aed", marginLeft: 8, fontWeight: 700 }}>· {selectedRows.length} selected</span>}
              </p>
            </div>
            <div style={{ display: "flex", gap: 10 }} className="no-print">
              <button onClick={handleExportCSV}
                style={{ background: "#16a34a", color: "#fff", border: "none", borderRadius: 10, padding: "9px 18px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                📥 Export CSV
              </button>
              <button onClick={handleDownloadPDF} disabled={submitting}
                style={{ background: "#7c3aed", color: "#fff", border: "none", borderRadius: 10, padding: "9px 18px", fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer", fontSize: 15 }}>
                {submitting ? "⏳ Processing..." : "📄 Download PDF"}
              </button>
              <button onClick={() => handlePrint()}
                style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 10, padding: "9px 18px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                🖨️ Print
              </button>
              <button
                onClick={handleSubmitReport}
                disabled={submitting || selectedRows.length === 0}
                style={{
                  background: selectedRows.length === 0 ? "#94a3b8" : submitting ? "#a78bfa" : "linear-gradient(135deg,#7c3aed,#6d28d9)",
                  color: "#fff", border: "none", borderRadius: 10, padding: "9px 20px", fontWeight: 800,
                  cursor: submitting || selectedRows.length === 0 ? "not-allowed" : "pointer", fontSize: 15
                }}>
                {submitting ? "⏳ Submitting..." : `📤 Submit Selected (${selectedRows.length})`}
              </button>
            </div>
          </div>

          {/* Filter */}
          <div style={{ background: "#fff", borderRadius: 14, padding: "16px 20px", boxShadow: "0 2px 10px rgba(15,37,87,0.07)", marginBottom: 16, display: "flex", gap: 14, alignItems: "flex-end" }} className="no-print">
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>📌 Filter by Status</label>
              <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setSelectedEntries({}); }}
                style={{ width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", cursor: "pointer" }}>
                <option value="">All Status</option>
                {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ flex: 3, background: "#eaf1ff", borderRadius: 10, padding: "10px 14px", fontSize: 14, color: "#2563eb" }}>
              ☑️ Use the <strong>checkbox</strong> to select the entries you want to submit, then click <strong>Submit Selected</strong>. Already submitted entries will be shown in <span style={{ color: "#16a34a", fontWeight: 700 }}>✅ green</span>.
            </div>
          </div>

          {/* Report Table */}
          <div ref={printRef} style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 10px rgba(15,37,87,0.07)", overflow: "hidden" }}>

            {/* Print header */}
            <div style={{ padding: "20px 24px", borderBottom: "2px solid #0f2557", display: "none" }} className="print-header">
              <div style={{ textAlign: "center" }}>
                <h2 style={{ color: "#0f2557", margin: 0 }}>Adhiparasakthi Dental College and Hospital</h2>
                <h3 style={{ color: "#2563eb", margin: "6px 0 0" }}>{activeSec?.label} Report</h3>
              </div>
              <div style={{ display: "flex", gap: 24, marginTop: 12, fontSize: 15 }}>
                <span><strong>Name:</strong> {profile?.name}</span>
                <span><strong>Emp ID:</strong> {profile?.empId}</span>
                <span><strong>Dept:</strong> {profile?.department}</span>
                <span><strong>Date:</strong> {new Date().toLocaleDateString()}</span>
              </div>
            </div>

            {filteredRows.length === 0 ? (
              <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>
                <div style={{ fontSize: 42, marginBottom: 12 }}>{activeSec?.icon}</div>
                <p>No {activeSec?.label} entries found.</p>
                <button onClick={() => navigate("/dashboard")} style={{ marginTop: 12, background: activeSec?.light, color: activeSec?.color, border: "none", borderRadius: 10, padding: "8px 20px", fontWeight: 700, cursor: "pointer" }}>
                  + Add Entries in Dashboard
                </button>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: activeSec?.light || "#f8fafc" }}>
                      {/* Select All checkbox */}
                      <th style={{ padding: "11px 14px", borderBottom: "2px solid #e2e8f0", width: 44, textAlign: "center" }} className="no-print">
                        <input
                          type="checkbox"
                          checked={allUnsubmittedSelected}
                          onChange={toggleSelectAll}
                          disabled={unsubmittedRows.length === 0}
                          title="Select all unsubmitted entries"
                          style={{ width: 16, height: 16, cursor: unsubmittedRows.length === 0 ? "not-allowed" : "pointer", accentColor: activeSec?.color || "#7c3aed" }}
                        />
                      </th>
                      {cols.slice(1).map(col => (
                        <th key={col} style={{ padding: "11px 12px", textAlign: "left", fontSize: 13, fontWeight: 700, color: activeSec?.color, letterSpacing: 0.5, borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" }}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map(({ siNo, entry, originalIndex }) => {
                      const isAlreadySubmitted = submittedIndexes.has(originalIndex);
                      const isChecked = !!selectedEntries[originalIndex];
                      return (
                        <tr
                          key={originalIndex}
                          onClick={() => !isAlreadySubmitted && toggleEntry(originalIndex)}
                          style={{
                            borderBottom: "1px solid #f1f5f9",
                            background: isAlreadySubmitted ? "#f0fdf4" : isChecked ? "#f5f3ff" : "transparent",
                            opacity: isAlreadySubmitted ? 0.75 : 1,
                            cursor: isAlreadySubmitted ? "default" : "pointer",
                            transition: "background 0.12s",
                          }}
                          onMouseEnter={e => { if (!isAlreadySubmitted && !isChecked) e.currentTarget.style.background = activeSec?.light || "#f8fafc"; }}
                          onMouseLeave={e => { if (!isAlreadySubmitted && !isChecked) e.currentTarget.style.background = "transparent"; }}
                        >
                          {/* Checkbox cell */}
                          <td style={{ padding: "10px 14px", textAlign: "center" }} className="no-print" onClick={e => e.stopPropagation()}>
                            {isAlreadySubmitted ? (
                              <span title="Already submitted" style={{ fontSize: 18 }}>✅</span>
                            ) : (
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleEntry(originalIndex)}
                                style={{ width: 16, height: 16, cursor: "pointer", accentColor: activeSec?.color || "#7c3aed" }}
                              />
                            )}
                          </td>
                          {/* Data cells */}
                          {keys.map((key, ki) => {
                            if (key === "si_no") return (
                              <td key={ki} style={{ padding: "10px 12px", color: isAlreadySubmitted ? "#16a34a" : "#64748b", fontWeight: 600 }}>
                                {siNo}
                              </td>
                            );
                            const val = getEntryValue(entry, key);
                            const isStatus = key.toLowerCase().includes("status");
                            return (
                              <td key={ki} style={{ padding: "10px 12px", color: "#475569", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {isStatus && val !== "—" ? (
                                  <span style={{
                                    background: val === "Published" || val === "Completed" || val === "Received" ? "#dcfce7" : val === "In Progress" || val === "Accepted" ? "#fef3c7" : "#f1f5f9",
                                    color: val === "Published" || val === "Completed" || val === "Received" ? "#16a34a" : val === "In Progress" || val === "Accepted" ? "#d97706" : "#64748b",
                                    borderRadius: 20, padding: "2px 8px", fontSize: 13, fontWeight: 700
                                  }}>
                                    {val}
                                  </span>
                                ) : (typeof val === "string" && val.startsWith("http") && val.includes("firebasestorage")) ? (
                                  <a href={val} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontWeight: 700, textDecoration: "none" }}>📎 View File</a>
                                ) : val}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Bottom sticky submit bar */}
                {selectedRows.length > 0 && (
                  <div style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)", color: "#fff", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }} className="no-print">
                    <span style={{ fontWeight: 700, fontSize: 15 }}>
                      ☑️ {selectedRows.length} {activeSec?.label} {selectedRows.length === 1 ? "entry" : "entries"} selected — ready to submit to admin
                    </span>
                    <button
                      onClick={handleSubmitReport}
                      disabled={submitting}
                      style={{ background: "#fff", color: "#7c3aed", border: "none", borderRadius: 8, padding: "7px 20px", fontWeight: 800, fontSize: 15, cursor: submitting ? "not-allowed" : "pointer" }}>
                      {submitting ? "⏳ Submitting..." : "📤 Submit Now"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      <style>{`
        @media print {
          header, aside, .no-print { display: none !important; }
          .print-header { display: block !important; }
          body { background: white; }
          table { font-size: 10px; }
          td, th { padding: 6px 8px !important; }
        }
      `}</style>
    </div>
  );
}
